import {
  GetAllQueryTrackerImportsDocument,
  GetAllQueryTrackerImportsQuery,
  ImportFileMutation
} from './../../generated/graphql';
import {
  Cache,
  UpdateResolver,
  DataFields,
  Variables,
  QueryInput
} from '@urql/exchange-graphcache';
import {
  AddCommentToQueryMutation,
  GetQueryDetailsDocument,
  GetQueryDetailsQuery,
  Mutation,
  QueryCommentInput
} from 'generated/graphql';

type MutationKeys = keyof Omit<Mutation, '__typename'>;
type MutationCacheUpdates = {
  [key in MutationKeys]: UpdateResolver;
};

export const updateQueryWithTypes = <Mutation, Query>(
  cache: Cache,
  input: QueryInput,
  result: any,
  fn: (m: Mutation, q: Query) => Query
) => cache.updateQuery(input, (data) => fn(result, data as any) as any);

/*
  when we fix our backend to return a whole entity after creating a new query,
  not only the query specialization, we could manually update the pages cache,
  but currently, the easy solution was to invalidate all pages
*/
const invalidateQueriesPages = (cache: Cache) => {
  const key = 'Query';
  cache
    .inspectFields(key)
    .filter((info) => info.fieldName === 'findBaseQueriesPage')
    .forEach((info) => cache.invalidate(key, info.fieldKey));
};

const invalidateSearchFilters = (cache: Cache) => cache.invalidate('Query', 'findSearchFilters');

export const mutationCacheUpdates: Partial<MutationCacheUpdates> = {
  deleteSearchFilter(_result: DataFields, _args: Variables, cache: Cache) {
    invalidateSearchFilters(cache);
  },
  updateSearchFilter(_result: DataFields, _args: Variables, cache: Cache) {
    invalidateSearchFilters(cache);
  },
  createSearchFilter(_result: DataFields, _args: Variables, cache: Cache) {
    invalidateSearchFilters(cache);
  },
  bulkUpdateSearchFilters(_result: DataFields, _args: Variables, cache: Cache) {
    invalidateSearchFilters(cache);
  },
  createDitaQuery(_result: DataFields, _args: Variables, cache: Cache) {
    invalidateQueriesPages(cache);
  },
  createNonAbapQuery(_result: DataFields, _args: Variables, cache: Cache) {
    invalidateQueriesPages(cache);
  },
  createTrainingQuery(_result: DataFields, _args: Variables, cache: Cache) {
    invalidateQueriesPages(cache);
  },
  addCommentToQuery(result: DataFields, args: Variables, cache: Cache) {
    const { queryId } = args.input as QueryCommentInput;
    updateQueryWithTypes<AddCommentToQueryMutation, GetQueryDetailsQuery>(
      cache,
      { query: GetQueryDetailsDocument, variables: { id: queryId } },
      result,
      (mutation, query) => {
        query.query?.comments.push(mutation.comment);
        return query;
      }
    );
  },
  importFile(result: DataFields, args: Variables, cache: Cache) {
    updateQueryWithTypes<ImportFileMutation, GetAllQueryTrackerImportsQuery>(
      cache,
      { query: GetAllQueryTrackerImportsDocument },
      result,
      (mutation, query) => {
        query.qtImports.unshift(mutation.importFile);
        return query;
      }
    );
  }
};
